/**
 * MyDraftsPanel (ported from Noteble — ADR-022).
 *
 * Collapsible panel showing locally saved drafts on the capture step.
 * Users can restore or delete drafts from here.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Image, StickyNote, FileText, Trash2, RotateCcw } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import type { DraftSummary } from '@/services/indexedDBDraftsService';

interface MyDraftsPanelProps {
  drafts: DraftSummary[];
  isLoading: boolean;
  hasAttemptedLoad: boolean;
  onRestore: (draftId: string) => void;
  onDelete: (draftId: string) => void;
  isRestoring?: boolean;
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export const MyDraftsPanel: React.FC<MyDraftsPanelProps> = ({
  drafts,
  isLoading,
  hasAttemptedLoad,
  onRestore,
  onDelete,
  isRestoring = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Don't render if we've loaded and confirmed there are no drafts
  if (hasAttemptedLoad && !isLoading && drafts.length === 0) return null;

  return (
    <div className="mt-6 border border-slate-200 dark:border-slate-700 rounded-xl bg-white/50 dark:bg-slate-900/50 overflow-hidden">
      {/* Header (always visible) */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            My drafts
          </span>
          {drafts.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300">
              {drafts.length}
            </span>
          )}
        </div>
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          {isLoading ? (
            <LoadingState size="md" />
          ) : drafts.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500 dark:text-slate-400">
              No drafts yet
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {drafts.map((draft) => (
                <div
                  key={draft.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  {/* Draft info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {draft.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {formatRelativeDate(draft.updatedAt)}
                      </span>
                      <div className="flex items-center gap-1">
                        {draft.photoCount > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-slate-500 dark:text-slate-400">
                            <Image className="w-3 h-3" />
                            {draft.photoCount}
                          </span>
                        )}
                        {draft.noteCount > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-slate-500 dark:text-slate-400">
                            <StickyNote className="w-3 h-3" />
                            {draft.noteCount}
                          </span>
                        )}
                        {draft.streamingTranscriptCount > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-slate-500 dark:text-slate-400">
                            <FileText className="w-3 h-3" />
                            {draft.streamingTranscriptCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 shrink-0">
                    {confirmDeleteId === draft.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            onDelete(draft.id);
                            setConfirmDeleteId(null);
                          }}
                          className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => onRestore(draft.id)}
                          disabled={isRestoring}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-600/10 rounded-md hover:bg-blue-600/15 transition-colors disabled:opacity-50"
                        >
                          {isRestoring ? (
                            <Spinner size="sm" tone="current" />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                          Restore
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(draft.id)}
                          className="inline-flex items-center p-1.5 text-slate-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                          title="Delete draft"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
